import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { API, Empty } from "./shared";

// ============================================================
// SUPPORT · Need-help floating pill (bottom-right, every page)
// Opens a modal with: category chips, subject, description, screenshot upload,
// and a "Chat on WhatsApp" fast-lane that deep-links to the support number
// with a prefilled message pointing at the current page.
// ============================================================
export function NeedHelpPill({ user, role }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    axios.get(`${API}/support/config`).then(r => setCfg(r.data)).catch(() => {});
  }, []);
  if (!user) return null;
  return (
    <>
      <button
        data-testid="need-help-pill"
        onClick={() => setOpen(true)}
        aria-label="Need help?"
        style={{position:"fixed",bottom:24,right:24,zIndex:1100,background:"#111827",color:"#FACC15",border:"none",padding:"12px 20px",borderRadius:999,fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:8,boxShadow:"0 10px 24px rgba(15,23,42,0.3)"}}
      >
        <MessageSquare size={16}/> Need help?
      </button>
      {open && <SupportModal cfg={cfg} user={user} role={role} onClose={() => setOpen(false)}/>}
    </>
  );
}


export function SupportModal({ cfg, user, role, onClose }) {
  const [cat, setCat] = useState("bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const CATS = [
    { code: "bug", label: "Bug", hint: "Something's broken" },
    { code: "payment", label: "Payment", hint: "Charge, escrow or refund" },
    { code: "how_to", label: "How-to", hint: "I'm stuck on a flow" },
    { code: "feature_request", label: "Idea", hint: "Would be great if…" },
    { code: "other", label: "Other", hint: "Something else" },
  ];

  const openWhatsApp = () => {
    const base = cfg?.whatsapp_url || "https://wa.me/918080664242";
    const msg = encodeURIComponent(
      `Hi GoPrintPack™ support — I need help.\n\nName: ${user?.name || "—"}\nEmail: ${user?.email || "—"}\nRole: ${role || "—"}\nPage: ${window.location.pathname}\n\nIssue:\n`
    );
    window.open(`${base}?text=${msg}`, "_blank", "noopener");
  };

  const submit = async () => {
    setErr("");
    if (subject.trim().length < 4) return setErr("Subject must be at least 4 characters.");
    if (description.trim().length < 4) return setErr("Please describe the issue a bit more.");
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        category: cat,
        subject: subject.trim(),
        description: description.trim(),
        page_url: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent.slice(0, 300),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        screenshot_file_ids: attachments.map(a => a.id || a.file_id).filter(Boolean),
      });
      toast.success("Ticket raised — our team will respond within 24h.");
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not raise ticket");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="support-modal" style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1200,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:14,width:640,maxWidth:"96vw",padding:"24px 28px",maxHeight:"92vh",overflow:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <h3 style={{margin:0,fontFamily:"'Fraunces', serif",fontSize:22}}>How can we help?</h3>
          <button onClick={onClose} aria-label="Close" style={{background:"transparent",border:"none",cursor:"pointer",fontSize:22,color:"#6B7280"}}>×</button>
        </div>
        <p style={{margin:"0 0 14px",fontSize:12,color:"#6B7280"}}>We respond within 24 hours (P0 payment issues within 2h). WhatsApp is fastest.</p>

        <button data-testid="support-whatsapp" onClick={openWhatsApp} style={{width:"100%",background:"#25D366",color:"#fff",border:"none",padding:"10px 16px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:14}}>
          <MessageSquare size={16}/> Chat on WhatsApp · {cfg?.whatsapp_number || "+91 80806 64242"}
        </button>

        <div style={{display:"flex",alignItems:"center",gap:8,margin:"4px 0 14px"}}>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
          <span style={{fontSize:10,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:"0.1em"}}>Or raise a ticket</span>
          <div style={{flex:1,height:1,background:"#E5E7EB"}}/>
        </div>

        {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Category</label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",margin:"6px 0 14px"}}>
          {CATS.map(c => (
            <button key={c.code} data-testid={`support-cat-${c.code}`} type="button" onClick={() => setCat(c.code)} style={{background: cat === c.code ? "#111827" : "#F3F4F6", color: cat === c.code ? "#FACC15" : "#374151", border:"none",padding:"6px 12px",borderRadius:999,fontSize:11,fontWeight:700,cursor:"pointer"}} title={c.hint}>{c.label}</button>
          ))}
        </div>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Subject</label>
        <input data-testid="support-subject" value={subject} onChange={e => setSubject(e.target.value.slice(0,200))} placeholder="One-line summary" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 14px"}}/>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>What happened?</label>
        <textarea data-testid="support-description" value={description} onChange={e => setDescription(e.target.value.slice(0,4000))} rows={4} placeholder="Walk us through what you were trying to do and what you expected…" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,margin:"6px 0 4px",fontFamily:"inherit"}}/>
        <small style={{fontSize:10,color:"#9CA3AF",display:"block",marginBottom:14}}>Email, phone and URLs will be auto-removed from this message.</small>

        <label style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase"}}>Screenshot (optional, up to 5)</label>
        <div style={{marginTop:6,marginBottom:14}}>
          <FileDropzone testid="support-dropzone" multiple accept="image/*,application/pdf" hint="PNG, JPG or PDF — up to 10 MB each" value={attachments} onChange={setAttachments}/>
        </div>

        <div style={{background:"#F9FAFB",padding:"10px 12px",borderRadius:8,fontSize:11,color:"#6B7280",marginBottom:14}}>
          We'll also auto-attach: page URL (<code>{window.location.pathname}</code>), your role, browser info and screen size — helps us debug faster.
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <button type="button" onClick={onClose} disabled={busy} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button data-testid="support-submit" type="button" onClick={submit} disabled={busy} style={{background: busy ? "#9CA3AF" : "#111827", color:"#FACC15", border:"none", padding:"9px 20px", borderRadius:8, fontSize:13, fontWeight:700, cursor: busy ? "not-allowed" : "pointer"}}>{busy ? "Sending…" : "Raise ticket"}</button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// SUPPORT · User-side "My tickets" sidebar page
// ============================================================
export function MyTickets({ user }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/support/tickets/mine`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load tickets"); setData({ items: [], unread: 0 }); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <section className="content"><Empty text="Loading tickets…"/></section>;

  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">SUPPORT · YOUR TICKETS</p>
          <h2>Every bug, question and request in one place</h2>
          <p className="subhead">Response SLA: 24h · P0 payment issues within 2h · WhatsApp support at +91 80806 64242.</p>
        </div>
      </div>
      {data.items.length === 0 ? (
        <Empty text='No tickets yet. Hit the "Need help?" pill (bottom-right) to raise one.'/>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"minmax(0,320px) 1fr",gap:16}}>
          <div style={{display:"grid",gap:8,alignContent:"start"}}>
            {data.items.map(t => (
              <button key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => setPicked(t)} style={{textAlign:"left",background: picked?.id === t.id ? "#F9FAFB" : "#fff",border: picked?.id === t.id ? "2px solid #111827" : "1px solid #E5E7EB",padding:"10px 12px",borderRadius:10,cursor:"pointer"}}>
                <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:3}}>
                  <span style={{background:"#111827",color:"#FACC15",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:800}}>{t.priority}</span>
                  <span style={{background:"#F3F4F6",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:700,color:"#374151"}}>{t.category}</span>
                  <span style={{background:"#F3F4F6",padding:"2px 6px",borderRadius:999,fontSize:9,color:"#374151"}}>{t.status}</span>
                  {t.unread_for_user && <span style={{background:"#DC2626",color:"#fff",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:800}}>NEW</span>}
                </div>
                <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{t.subject}</div>
                <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{new Date(t.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</div>
              </button>
            ))}
          </div>
          <div>{picked ? <TicketView ticketId={picked.id} viewerRole="user" onChanged={load}/> : <Empty text="Pick a ticket to see the thread."/>}</div>
        </div>
      )}
    </section>
  );
}


// ============================================================
// SUPPORT · Shared ticket thread view (used by user + admin side)
// ============================================================
export function TicketView({ ticketId, viewerRole, onChanged }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/support/tickets/${ticketId}`);
      setT(data);
    } catch { setT(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, { message: reply.trim() });
      setReply("");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply"); }
    finally { setBusy(false); }
  };

  const changeStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/support/tickets/${ticketId}`, { status });
      await load();
      onChanged && onChanged();
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
  };

  if (!t) return <Empty text="Loading ticket…"/>;

  return (
    <div data-testid="ticket-view" style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:16}}>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:8}}>
        <span style={{background:"#111827",color:"#FACC15",padding:"3px 8px",borderRadius:999,fontSize:10,fontWeight:800}}>{t.priority}</span>
        <span style={{background:"#F3F4F6",padding:"3px 8px",borderRadius:999,fontSize:10,fontWeight:700,color:"#374151"}}>{t.category}</span>
        <span style={{background:"#F3F4F6",padding:"3px 8px",borderRadius:999,fontSize:10,color:"#374151"}}>{t.status}</span>
        {viewerRole === "admin" && t.user_name && <span style={{fontSize:11,color:"#6B7280"}}>· from <b>{t.user_name}</b> ({t.user_role})</span>}
      </div>
      <h3 style={{margin:"0 0 8px",fontSize:17,fontWeight:700}}>{t.subject}</h3>
      <p style={{margin:"0 0 12px",fontSize:13,color:"#374151",whiteSpace:"pre-wrap"}}>{t.description}</p>
      {t.page_url && <div style={{fontSize:11,color:"#6B7280",marginBottom:8}}>Reported from: <code>{t.page_url}</code>{t.viewport ? ` · ${t.viewport}` : ""}</div>}
      {(t.attachments || []).length > 0 && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",margin:"6px 0 14px"}}>
          {t.attachments.map((a, i) => (
            <a key={i} href={a.url || a.file_url || a} target="_blank" rel="noreferrer" style={{display:"inline-block",padding:"6px 10px",background:"#F3F4F6",color:"#111827",borderRadius:8,fontSize:11,fontWeight:600,textDecoration:"none"}}>📎 {a.name || `Attachment ${i+1}`}</a>
          ))}
        </div>
      )}

      <div style={{borderTop:"1px solid #E5E7EB",paddingTop:12,marginTop:4}}>
        <div style={{fontSize:11,fontWeight:700,color:"#6B7280",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Thread · {t.replies?.length || 0} message{(t.replies?.length || 0) === 1 ? "" : "s"}</div>
        <div style={{display:"grid",gap:8,maxHeight:300,overflow:"auto",marginBottom:12}}>
          {(t.replies || []).map(r => (
            <div key={r.id} style={{padding:"8px 12px",background: r.author_role === "admin" ? "#FEF3C7" : "#F9FAFB",borderRadius:8,border:"1px solid " + (r.author_role === "admin" ? "#FDE68A" : "#E5E7EB")}}>
              <div style={{fontSize:11,fontWeight:700,color: r.author_role === "admin" ? "#92400E" : "#111827",marginBottom:3}}>{r.author_role === "admin" ? "GoPrintPack™ Support" : (r.author_name || "You")} <span style={{color:"#9CA3AF",fontWeight:400,marginLeft:6}}>· {new Date(r.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span></div>
              <div style={{fontSize:13,color:"#374151",whiteSpace:"pre-wrap"}}>{r.message}</div>
            </div>
          ))}
        </div>
        {t.status !== "closed" && (
          <>
            <textarea data-testid="ticket-reply-input" value={reply} onChange={e => setReply(e.target.value.slice(0,4000))} rows={3} placeholder="Type your reply…" style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:13,fontFamily:"inherit"}}/>
            <div style={{display:"flex",justifyContent:"space-between",gap:8,marginTop:8,flexWrap:"wrap"}}>
              {viewerRole === "admin" ? (
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {["in_progress","waiting_user","resolved","closed"].map(s => s !== t.status && (
                    <button key={s} data-testid={`ticket-status-${s}`} onClick={() => changeStatus(s)} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"6px 10px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer"}}>→ {s.replace(/_/g," ")}</button>
                  ))}
                </div>
              ) : <span/>}
              <button data-testid="ticket-reply-send" onClick={sendReply} disabled={busy || !reply.trim()} style={{background: (busy || !reply.trim()) ? "#9CA3AF" : "#111827",color:"#FACC15",border:"none",padding:"8px 18px",borderRadius:8,fontSize:12,fontWeight:700,cursor:(busy || !reply.trim()) ? "not-allowed" : "pointer"}}>{busy ? "Sending…" : "Send reply"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================
// SUPPORT · Admin inbox
// ============================================================
export function AcSupport() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [picked, setPicked] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    try {
      const { data } = await axios.get(`${API}/admin/support/tickets?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load tickets"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  if (!data) return <Empty text="Loading tickets…"/>;

  const STATUS_OPTS = ["", "open", "in_progress", "waiting_user", "resolved", "closed"];
  return (
    <div data-testid="ac-support" style={{marginTop:20}}>
      <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        {[
          { label: "Unread", value: data.unread_admin },
          { label: "Open", value: data.totals?.open || 0 },
          { label: "In progress", value: data.totals?.in_progress || 0 },
          { label: "Waiting user", value: data.totals?.waiting_user || 0 },
          { label: "Resolved", value: data.totals?.resolved || 0 },
        ].map(p => (
          <div key={p.label} style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:10,padding:"10px 14px",flex:"1 1 140px"}}>
            <div style={{fontSize:10,color:"#6B7280",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>{p.label}</div>
            <div style={{fontSize:18,fontWeight:700,color:"#111827",marginTop:3}}>{p.value}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
        {STATUS_OPTS.map(s => (
          <button key={s || "all"} onClick={() => setStatus(s)} style={{background: status === s ? "#111827" : "#F3F4F6", color: status === s ? "#FACC15" : "#374151", border:"none", padding:"6px 12px", borderRadius:999, fontSize:11, fontWeight:700, cursor:"pointer"}}>{s || "All"}</button>
        ))}
      </div>
      {(data.items || []).length === 0 ? (
        <Empty text="No tickets match that filter."/>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"minmax(0,340px) 1fr",gap:16}}>
          <div style={{display:"grid",gap:8,alignContent:"start",maxHeight:"68vh",overflow:"auto"}}>
            {data.items.map(t => (
              <button key={t.id} data-testid={`ac-ticket-${t.id}`} onClick={() => setPicked(t)} style={{textAlign:"left",background: picked?.id === t.id ? "#F9FAFB" : "#fff",border: picked?.id === t.id ? "2px solid #111827" : "1px solid #E5E7EB",padding:"10px 12px",borderRadius:10,cursor:"pointer"}}>
                <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:3,flexWrap:"wrap"}}>
                  <span style={{background:"#111827",color:"#FACC15",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:800}}>{t.priority}</span>
                  <span style={{background:"#F3F4F6",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:700,color:"#374151"}}>{t.category}</span>
                  <span style={{background:"#F3F4F6",padding:"2px 6px",borderRadius:999,fontSize:9,color:"#374151"}}>{t.status}</span>
                  {t.unread_for_admin && <span style={{background:"#DC2626",color:"#fff",padding:"2px 6px",borderRadius:999,fontSize:9,fontWeight:800}}>NEW</span>}
                </div>
                <div style={{fontSize:13,fontWeight:600,color:"#111827"}}>{t.subject}</div>
                <div style={{fontSize:11,color:"#6B7280",marginTop:3}}>{t.user_name} · {t.user_role}</div>
                <div style={{fontSize:10,color:"#9CA3AF",marginTop:3}}>{new Date(t.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</div>
              </button>
            ))}
          </div>
          <div>{picked ? <TicketView ticketId={picked.id} viewerRole="admin" onChanged={load}/> : <Empty text="Pick a ticket to see the thread."/>}</div>
        </div>
      )}
    </div>
  );
}
